import type { IconName } from '../components/icons';
import type { TKey } from '../i18n/dictionary';
import { resolveState, STATES, TIER, type StateDef, type TierTokens } from './states';
import { TRANSFERS, type TransferDef } from './transfers';
import { composition, strictestMode, TEMP_STYLE } from './temps';
import type { OrderPackage, PackageInfo, StateId, Tier } from './types';

/**
 * ★ Jádro obrazovky.
 *
 * Veškerá rozhodovací logika specu žije TADY, ne v komponentách. Komponenty
 * dostanou hotový `ScreenModel` a už nic nerozhodují. Důvod: pravidla ve specu
 * jsou provázaná (převoz mění pořadí prvků i obsah verdiktu, potvrzený převoz
 * mizí úplně, jednokusová zásilka skrývá dva bloky…). Rozpuštěná do `{cond && …}`
 * napříč deseti komponentami by nešla ani otestovat, ani bezpečně změnit.
 */

export interface ScreenModel {
  tier: Tier;
  stateId: StateId;
  tokens: TierTokens;
  /** Podbarvení stránky a lišty (zohledňuje volitelný tint podle převozu). */
  pageBg: string;
  barBg: string;

  banner: { direction: 'CZ' | 'SK'; textKey: TKey; bg: string; loadToday: boolean } | null;

  /** Okno s datem – tři podoby podle specu 3. */
  untilBox: {
    /** Při převozu jde okno NAD verdikt. */
    aboveVerdict: boolean;
    bg: string;
    icon: IconName;
    labelKey: TKey;
    warehouseCode: string | null;
    /** Hotová hodnota k vypsání, nebo klíč pro „Ještě neurčen“. */
    value: string | null;
    valueKey: TKey | null;
  } | null;

  verdict: {
    icon: IconName;
    accent: string;
    kickerKey: TKey;
    /** Když je true, headline je název stavu; jinak se použije headlineKey. */
    useStateName: boolean;
    warnHeadline: boolean;
    /** Popis stavu – při převozu se zahazuje (spec 3). */
    showSub: boolean;
    /** Před popisem se vypíše tučně název stavu. */
    showNameInSub: boolean;
  };

  chips: { key: TKey; icon: IconName; variant: 'retry' | 'part' }[];

  /** Karta se u vrstvy NOTFOUND nezobrazuje – žádná data neexistují. */
  showCard: boolean;
  showCodeBox: boolean;

  tempBand: {
    bg: string;
    ink: string;
    icon: IconName;
    /** Složení k vypsání (`1× CHLAZENÉ + 2× MRAŽENÉ`), nebo null pro prostý název režimu. */
    compose: { chilled: number; frozen: number } | null;
  } | null;

  completionBand: { complete: boolean; missing: number[] } | null;

  /** Vícekusová zásilka – ovlivňuje meta grid i pás kompletace. */
  isMultiPackage: boolean;
  /** Počet balíků objednávky; null = objednávka není rozpadlá na balíky. */
  packageTotal: number | null;

  scanCountBadge: { count: number; warn: boolean } | null;

  actions: {
    variant: 'scan' | 'stop' | 'notfound' | 'transfer';
    /** Skrývá se u vrstvy STOP – u zablokované zásilky nemá smysl. */
    showNavigate: boolean;
    showEditWeight: boolean;
  };

  sound: 'go' | 'neutral' | 'stop' | 'edge';
}

export interface ResolveContext {
  /** Převoz potvrzený v této relaci; při dalším skenu se spoléhá na backend (A.4). */
  transferConfirmedLocally: boolean;
  /** Volitelné podbarvení stránky podle převozu – spec 4b NEDOPORUČUJE zapínat. */
  tintPageByTransfer?: boolean;
}

/** Nenalezená zásilka (404) – vlastní vrstva, ne chybová hláška z API. */
export function resolveNotFound(): ScreenModel {
  return buildFromState(STATES.nenalezena, null, null, {
    transferConfirmedLocally: false,
  });
}

export function resolveScreen(pkg: PackageInfo, ctx: ResolveContext): ScreenModel {
  const state = resolveState(pkg.statusCode, pkg.statusLabelRaw);
  return buildFromState(state, pkg, null, ctx);
}

function buildFromState(
  state: StateDef,
  pkg: PackageInfo | null,
  _unused: null,
  ctx: ResolveContext,
): ScreenModel {
  const tokens = TIER[state.tier];
  const isStop = state.tier === 'stop';
  const isNotFound = state.tier === 'notfound';

  // Po potvrzení převozu se zásilka přestane chovat jako zásilka na převozu –
  // banner zmizí, verdikt se vrátí k normálnímu popisu, okno se přepne na azurové.
  const rawTransfer = pkg?.transfer ?? null;
  const transferActive =
    !!rawTransfer && !rawTransfer.confirmed && !ctx.transferConfirmedLocally && !isNotFound;
  const transfer: TransferDef | null = transferActive ? TRANSFERS[rawTransfer!.direction] : null;

  // Převoz mění chování jen u modrých (budoucích) stavů.
  const needsTransfer = state.tier === 'neutral' && !!transfer;

  const tintByTransfer = !!ctx.tintPageByTransfer && !!transfer && !isStop && !isNotFound;

  // Jediný zdroj pravdy o kusovosti: pole balíků. Počet kusů, chybějící kusy
  // i složení teplot jsou jeho projekce – nemají jak si navzájem odporovat.
  const packages = pkg?.order.packages ?? null;
  const total = packages?.length ?? null;
  const isMultiPackage = (total ?? 1) > 1;

  return {
    tier: state.tier,
    stateId: state.id,
    tokens,
    pageBg: tintByTransfer ? transfer!.tint : tokens.page,
    barBg: tintByTransfer ? transfer!.barTint : tokens.bar,

    banner: transfer
      ? {
          direction: transfer.direction,
          textKey: transfer.bannerKey,
          bg: transfer.bg,
          // Odznak NALOŽIT DNES se zobrazuje jen u zelené vrstvy – zásilka jede teď.
          loadToday: transfer.loadToday && state.tier === 'go',
        }
      : null,

    untilBox: buildUntilBox(state, pkg, transfer, needsTransfer),

    verdict: {
      icon: state.icon,
      accent: state.accent,
      kickerKey: `s.${state.id}.kicker` as TKey,
      // Při převozu je ve verdiktu JEN název stavu – žádný popis, žádný pokyn.
      // U Schválení Přepravy tedy nesvítí ani ZÍTRA NEJEDE.
      useStateName: needsTransfer || !hasHeadline(state.id),
      warnHeadline: !needsTransfer && !!state.warnHl,
      // ★ Při převozu je ve verdiktu JEN název stavu – žádný popis, žádný
      // pokyn, ani řádek „Zásilku nutno převézt do SR“. Informaci o převozu
      // nese banner nahoře a indigo okno s expedicí; ve verdiktu by byla
      // potřetí. Spec v20 s tím počítá i u `Vrácení Odesilateli`: věta
      // „Není to konečný stav.“ tam mizí a rozdíl proti červenému
      // `Vráceno Odesilateli` nese barva a ikona.
      showSub: !needsTransfer,
      showNameInSub: !needsTransfer && hasHeadline(state.id) && !state.noName,
    },

    chips: buildChips(pkg, isNotFound),

    showCard: !isNotFound,
    showCodeBox: isNotFound,

    tempBand: buildTempBand(pkg, packages, isNotFound, isMultiPackage),

    // U jednokusové zásilky by „1 z 1 kompletní“ byl jen šum – pás se skrývá.
    completionBand:
      !isNotFound && isMultiPackage && packages ? buildCompletionBand(packages) : null,

    isMultiPackage,
    packageTotal: total,

    scanCountBadge:
      !isNotFound && typeof pkg?.scanCount === 'number'
        ? { count: pkg.scanCount, warn: pkg.scanCount >= 2 }
        : null,

    actions: {
      variant: isNotFound ? 'notfound' : isStop ? 'stop' : needsTransfer ? 'transfer' : 'scan',
      showNavigate: !isStop && !isNotFound,
      showEditWeight: !isStop && !isNotFound,
    },

    sound: soundFor(state.tier),
  };
}

/** Stavy, které mají v hlavičce pokyn místo názvu stavu (ZÍTRA NEJEDE, CHYBA ZÁSILKY…). */
function hasHeadline(id: StateId): boolean {
  return (
    id === 'schvaleni' ||
    id === 'dokoncena' ||
    id === 'zrusena' ||
    id === 'vraceno' ||
    id === 'nenalezena'
  );
}

function buildUntilBox(
  state: StateDef,
  pkg: PackageInfo | null,
  transfer: TransferDef | null,
  needsTransfer: boolean,
): ScreenModel['untilBox'] {
  if (!state.until || !pkg) return null;

  if (needsTransfer && transfer) {
    // Indigo okno BUDE EXPEDOVÁNO ZE SKLADU [SK] – jde NAD verdikt,
    // protože u přeshraniční zásilky je dominantní fakt „jde do druhé země“.
    return {
      aboveVerdict: true,
      bg: '#5925DC',
      icon: 'border',
      labelKey: 'expedFrom',
      warehouseCode: pkg.transfer?.warehouseCode ?? transfer.direction,
      value: state.untilUnknown ? null : formatDate(pkg.expeditionAt ?? pkg.deliverAt),
      valueKey: state.untilUnknown ? 'notSet' : null,
    };
  }

  return {
    aboveVerdict: false,
    bg: '#175CD3',
    icon: 'cal',
    labelKey: state.untilUnknown ? 'dayLbl' : 'until',
    warehouseCode: null,
    value: state.untilUnknown ? null : formatDate(pkg.deliverAt),
    valueKey: state.untilUnknown ? 'notSet' : null,
  };
}

function buildChips(pkg: PackageInfo | null, isNotFound: boolean): ScreenModel['chips'] {
  if (!pkg || isNotFound) return [];
  const chips: ScreenModel['chips'] = [];
  if (pkg.flags.isRetryAttempt) chips.push({ key: 'retry', icon: 'warn', variant: 'retry' });
  if (pkg.flags.isSecondPart) chips.push({ key: 'part', icon: 'split', variant: 'part' });
  return chips;
}

/**
 * Pás kompletace. Chybí to, co nikdo nenaskenoval – `scannedAt === null`.
 *
 * Pořadí čísel drží backend (kontrakt: `packages` vzestupně podle `number`),
 * aplikace je nepřerovnává.
 */
function buildCompletionBand(packages: OrderPackage[]): ScreenModel['completionBand'] {
  const missing = packages.filter((p) => p.scannedAt == null).map((p) => p.number);
  return { complete: missing.length === 0, missing };
}

function buildTempBand(
  pkg: PackageInfo | null,
  packages: OrderPackage[] | null,
  isNotFound: boolean,
  isMulti: boolean,
): ScreenModel['tempBand'] {
  if (!pkg || isNotFound) return null;
  // Složení má smysl jen u vícekusové zásilky (spec 5a) – u jednokusové se
  // ani nepočítá. Díky poli balíků je „jeden kus, ale smíšené složení“
  // rovnou nereprezentovatelný stav, ne jen ošetřená výjimka.
  const comp = isMulti && packages ? composition(packages) : null;
  const mode = strictestMode(pkg.temperature, comp);
  // Bez režimu (ambient) se pás nezobrazí – ne prázdný pás.
  if (!mode) return null;

  const style = TEMP_STYLE[mode];
  const mixed = !!comp && comp.chilled > 0 && comp.frozen > 0;

  return {
    bg: style.bg,
    ink: style.ink,
    icon: style.icon,
    compose: mixed ? { chilled: comp.chilled, frozen: comp.frozen } : null,
  };
}

function soundFor(tier: Tier): ScreenModel['sound'] {
  if (tier === 'go') return 'go';
  if (tier === 'stop') return 'stop';
  if (tier === 'unknown') return 'edge';
  return 'neutral';
}

/** `13. 8. 2026` – český formát bez nul na začátku. */
export function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}
